import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { setGlobalLoading } from '../../../store/slices/uiSlice';
import { updateJobPocWithEdgeFunction } from '../../Dashboard/api/jobsWithEdgeFunction';
import { createUserWithEdgeFunction, deleteUserWithEdgeFunction } from '../api/adminWithEdgeFunction';
import { CreateUserPayload, Job } from '../../../types';
import { selectPocs } from '../../../store/selectors/adminSelectors';

export const useAdminActions = (
  refreshUsers: () => Promise<void>,
  refreshJobs: () => Promise<void>,
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>
) => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const pocs = useSelector(selectPocs);

  const handleReassignPoc = async (jobId: string, newPocId: string) => {
    dispatch(setGlobalLoading({ isLoading: true }));
    const newPoc = pocs.find((poc) => poc.id === newPocId);
    const newPocName = newPoc?.name || '—';

    // Optimistic update
    setJobs((prevJobs) =>
      prevJobs.map((j) =>
        j.job_id === jobId ? { ...j, poc_id: newPocId, poc_name: newPocName } : j
      )
    );

    try {
      const { error } = await updateJobPocWithEdgeFunction(jobId, newPocId);
      if (error) {
        showSnackbar('Failed to reassign POC', 'error');
        await refreshJobs(); // Revert
      } else {
        showSnackbar('POC reassigned successfully', 'success');
      }
    } catch (err: any) {
      showSnackbar('Failed to reassign POC', 'error');
      await refreshJobs(); // Revert
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  };

  const handleAddUser = async (newUser: CreateUserPayload): Promise<{ success: boolean; error?: string }> => {
    const email = (newUser.email || '').trim().toLowerCase();
    const full_name = (newUser.full_name || '').trim();

    if (!email || !full_name) {
      return { success: false, error: 'Name and email are required' };
    }

    if (!newUser.outlet_id) {
      return { success: false, error: 'Outlet selection is required' };
    }

    if (!newUser.role) {
      return { success: false, error: 'Role selection is required' };
    }

    dispatch(setGlobalLoading({ isLoading: true }));

    try {
      const userData: CreateUserPayload = {
        email,
        full_name,
        outlet_id: newUser.outlet_id,
        organization_id: newUser.organization_id,
        role: newUser.role,
      };

      const { data, error } = await createUserWithEdgeFunction(userData);

      if (error) {
        showSnackbar('Failed to create user', 'error');
        return { success: false, error: error.error || 'Failed to create user' };
      }

      if (data) {
        showSnackbar('User created successfully', 'success');
        await refreshUsers();
        return { success: true };
      }
      return { success: false, error: 'Unknown error' };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create user';
      showSnackbar('Failed to create user', 'error');
      return { success: false, error: errorMessage };
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  };

  const handleDeletePoc = async (userId: string): Promise<{ success: boolean; error?: string; errorCode?: string }> => {
    dispatch(setGlobalLoading({ isLoading: true }));

    try {
      const { error } = await deleteUserWithEdgeFunction(userId);

      if (error) {
        // Return specific error code for handling in UI (e.g. showing modal message)
        if (error.error === 'NO_MANAGER_FOR_OUTLET' || error.error === 'NO_ACTIVE_POC_FOR_JOB') {
          return { success: false, error: error.message, errorCode: error.error };
        }
        showSnackbar('Failed to delete user', 'error');
        return { success: false, error: error.message };
      }

      showSnackbar('User deleted successfully', 'success');
      await refreshUsers();
      return { success: true };
    } catch (err: any) {
      showSnackbar('Failed to delete user', 'error');
      return { success: false, error: err.message };
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  };

  return { handleReassignPoc, handleAddUser, handleDeletePoc };
};
